import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Pencil, Trash2, RotateCcw, Save,
  // Home & Building
  Droplets, Zap, Wind, Sparkles, Hammer, Tv, PaintBucket, Wrench,
  Package, Car, Bug, Leaf, Truck, Monitor, Home, Building, Building2, Warehouse,
  // Tools & Repairs
  Flame, Shield, Scissors, Heart, Cog, Brush, Pipette, Plug, Drill, Ruler, 
  Paintbrush, Key, Lock, Fan, Thermometer, Gauge, Settings,
  // Nature & Garden
  TreeDeciduous, Flower2, Sprout, Sun, CloudRain, Umbrella, Snowflake,
  // Tech & Electronics
  Laptop, Smartphone, Wifi, Printer, Camera, Speaker, Headphones,
  // Lifestyle & Services
  Utensils, Baby, Dog, Cat, Dumbbell, Stethoscope, Briefcase,
  // Other
  Gift, Star, Crown, Gem, ShoppingBag, Coffee, Wine, Pizza, Sofa, Bed,
  Bath, Shirt, Watch, Glasses, GraduationCap, Music, Book
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const availableIcons = [
  // Most Common - Home Services
  { name: 'Droplets', component: Droplets, label: 'Water/Plumbing' },
  { name: 'Zap', component: Zap, label: 'Electrical' },
  { name: 'Wind', component: Wind, label: 'Aircon/HVAC' },
  { name: 'Sparkles', component: Sparkles, label: 'Cleaning' },
  { name: 'Hammer', component: Hammer, label: 'Carpentry' },
  { name: 'Paintbrush', component: Paintbrush, label: 'Painting' },
  { name: 'Wrench', component: Wrench, label: 'Repairs' },
  { name: 'Tv', component: Tv, label: 'Appliances' },
  { name: 'Bug', component: Bug, label: 'Pest Control' },
  { name: 'Leaf', component: Leaf, label: 'Garden/Landscaping' },
  { name: 'Truck', component: Truck, label: 'Moving/Delivery' },
  { name: 'Home', component: Home, label: 'Home/Roofing' },
  
  // Tools & Repairs
  { name: 'Drill', component: Drill, label: 'Power Tools' },
  { name: 'Cog', component: Cog, label: 'Mechanical' },
  { name: 'Settings', component: Settings, label: 'Settings' },
  { name: 'Key', component: Key, label: 'Locksmith' },
  { name: 'Lock', component: Lock, label: 'Security' },
  { name: 'Plug', component: Plug, label: 'Electrical Plug' },
  { name: 'Thermometer', component: Thermometer, label: 'Temperature' },
  { name: 'Fan', component: Fan, label: 'Ventilation' },
  { name: 'Gauge', component: Gauge, label: 'Measurement' },
  { name: 'Ruler', component: Ruler, label: 'Construction' },
  { name: 'PaintBucket', component: PaintBucket, label: 'Paint Bucket' },
  { name: 'Brush', component: Brush, label: 'Brush' },
  { name: 'Pipette', component: Pipette, label: 'Chemicals' },
  
  // Building & Property
  { name: 'Building', component: Building, label: 'Building' },
  { name: 'Building2', component: Building2, label: 'Office' },
  { name: 'Warehouse', component: Warehouse, label: 'Warehouse' },
  
  // Nature & Outdoor
  { name: 'TreeDeciduous', component: TreeDeciduous, label: 'Tree' },
  { name: 'Flower2', component: Flower2, label: 'Flower' },
  { name: 'Sprout', component: Sprout, label: 'Plant' },
  { name: 'Sun', component: Sun, label: 'Sun/Solar' },
  { name: 'CloudRain', component: CloudRain, label: 'Weather' },
  { name: 'Umbrella', component: Umbrella, label: 'Protection' },
  { name: 'Snowflake', component: Snowflake, label: 'Cooling' },
  { name: 'Flame', component: Flame, label: 'Fire/Heating' },
  
  // Tech & Electronics
  { name: 'Monitor', component: Monitor, label: 'Computer' },
  { name: 'Laptop', component: Laptop, label: 'Laptop' },
  { name: 'Smartphone', component: Smartphone, label: 'Phone' },
  { name: 'Wifi', component: Wifi, label: 'Network' },
  { name: 'Printer', component: Printer, label: 'Printer' },
  { name: 'Camera', component: Camera, label: 'Camera' },
  { name: 'Speaker', component: Speaker, label: 'Audio' },
  { name: 'Headphones', component: Headphones, label: 'Headphones' },
  
  // Vehicles & Transport
  { name: 'Car', component: Car, label: 'Automotive' },
  { name: 'Package', component: Package, label: 'Package' },
  
  // Lifestyle & Personal
  { name: 'Heart', component: Heart, label: 'Health/Beauty' },
  { name: 'Scissors', component: Scissors, label: 'Salon' },
  { name: 'Utensils', component: Utensils, label: 'Food/Kitchen' },
  { name: 'Baby', component: Baby, label: 'Childcare' },
  { name: 'Dog', component: Dog, label: 'Pet Care' },
  { name: 'Cat', component: Cat, label: 'Cat Care' },
  { name: 'Dumbbell', component: Dumbbell, label: 'Fitness' },
  { name: 'Stethoscope', component: Stethoscope, label: 'Medical' },
  { name: 'Briefcase', component: Briefcase, label: 'Business' },
  { name: 'Shield', component: Shield, label: 'Security' },
  
  // Furniture & Interior
  { name: 'Sofa', component: Sofa, label: 'Furniture' },
  { name: 'Bed', component: Bed, label: 'Bedroom' },
  { name: 'Bath', component: Bath, label: 'Bathroom' },
  { name: 'Shirt', component: Shirt, label: 'Laundry' },
  
  // Other Services
  { name: 'Coffee', component: Coffee, label: 'Cafe' },
  { name: 'Wine', component: Wine, label: 'Beverage' },
  { name: 'Pizza', component: Pizza, label: 'Food Delivery' },
  { name: 'Gift', component: Gift, label: 'Gift' },
  { name: 'Star', component: Star, label: 'Premium' },
  { name: 'Crown', component: Crown, label: 'VIP' },
  { name: 'Gem', component: Gem, label: 'Luxury' },
  { name: 'ShoppingBag', component: ShoppingBag, label: 'Shopping' },
  { name: 'Watch', component: Watch, label: 'Time' },
  { name: 'Glasses', component: Glasses, label: 'Optical' },
  { name: 'GraduationCap', component: GraduationCap, label: 'Education' },
  { name: 'Music', component: Music, label: 'Entertainment' },
  { name: 'Book', component: Book, label: 'Learning' },
];

const availableColors = [
  // Blues
  { name: 'blue', bg: 'bg-blue-100', text: 'text-blue-600', label: 'Blue' },
  { name: 'sky', bg: 'bg-sky-100', text: 'text-sky-600', label: 'Sky' },
  { name: 'cyan', bg: 'bg-cyan-100', text: 'text-cyan-600', label: 'Cyan' },
  { name: 'teal', bg: 'bg-teal-100', text: 'text-teal-600', label: 'Teal' },
  { name: 'indigo', bg: 'bg-indigo-100', text: 'text-indigo-600', label: 'Indigo' },
  
  // Greens
  { name: 'emerald', bg: 'bg-emerald-100', text: 'text-emerald-600', label: 'Emerald' },
  { name: 'green', bg: 'bg-green-100', text: 'text-green-600', label: 'Green' },
  { name: 'lime', bg: 'bg-lime-100', text: 'text-lime-600', label: 'Lime' },
  
  // Warm colors
  { name: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-600', label: 'Yellow' },
  { name: 'amber', bg: 'bg-amber-100', text: 'text-amber-600', label: 'Amber' },
  { name: 'orange', bg: 'bg-orange-100', text: 'text-orange-600', label: 'Orange' },
  { name: 'red', bg: 'bg-red-100', text: 'text-red-600', label: 'Red' },
  { name: 'rose', bg: 'bg-rose-100', text: 'text-rose-600', label: 'Rose' },
  
  // Pinks & Purples
  { name: 'pink', bg: 'bg-pink-100', text: 'text-pink-600', label: 'Pink' },
  { name: 'fuchsia', bg: 'bg-fuchsia-100', text: 'text-fuchsia-600', label: 'Fuchsia' },
  { name: 'purple', bg: 'bg-purple-100', text: 'text-purple-600', label: 'Purple' },
  { name: 'violet', bg: 'bg-violet-100', text: 'text-violet-600', label: 'Violet' },
  
  // Neutrals
  { name: 'slate', bg: 'bg-slate-100', text: 'text-slate-600', label: 'Slate' },
  { name: 'gray', bg: 'bg-gray-100', text: 'text-gray-600', label: 'Gray' },
  { name: 'stone', bg: 'bg-stone-100', text: 'text-stone-600', label: 'Stone' },
  { name: 'zinc', bg: 'bg-zinc-100', text: 'text-zinc-600', label: 'Zinc' },
];

export default function AdminCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categoryStats, setCategoryStats] = useState({}); // {categoryId: {count, lowestPrice}}

  const [catForm, setCatForm] = useState({
    name: '', description: '', icon: 'Wrench', image_url: '', icon_color: 'blue'
  });

  const fetchData = useCallback(async () => {
    try {
      const catRes = await axios.get(`${API}/admin/categories`);
      setCategories(catRes.data);
      
      // Stats are now included in the response - no need for N+1 queries
      const stats = {};
      for (const cat of catRes.data) {
        stats[cat.id] = {
          count: cat.service_count || 0,
          lowestPrice: cat.lowest_price || null
        };
      }
      setCategoryStats(stats);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAddCategory = () => {
    setEditingCategory(null);
    setCatForm({ name: '', description: '', icon: 'Wrench', image_url: '', icon_color: 'blue' });
    setShowDialog(true);
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCatForm({ 
      name: cat.name, 
      description: cat.description, 
      icon: cat.icon, 
      image_url: cat.image_url || '',
      icon_color: cat.icon_color || 'slate'
    });
    setShowDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!catForm.name || !catForm.description) {
      toast.error('Name and description are required');
      return;
    }
    setSaving(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, catForm);
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/admin/categories`, catForm);
        toast.success('Category created');
      }
      setShowDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Deactivate this category?')) return;
    try {
      await axios.delete(`${API}/admin/categories/${catId}`);
      toast.success('Category deactivated');
      fetchData();
    } catch (e) {
      toast.error('Failed to deactivate');
    }
  };

  const handleRestoreCategory = async (catId) => {
    try {
      await axios.put(`${API}/admin/categories/${catId}/restore`);
      toast.success('Category restored');
      fetchData();
    } catch (e) {
      toast.error('Failed to restore');
    }
  };

  const handlePermanentDeleteCategory = async (catId, catName) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE "${catName}"? This action cannot be undone!`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${catId}/permanent`);
      toast.success('Category permanently deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete permanently');
    }
  };

  const activeCategories = categories.filter(c => c.is_active !== false);
  const inactiveCategories = categories.filter(c => c.is_active === false);
  const usedIcons = activeCategories.map(c => c.icon);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 p-4 md:p-8 md:ml-64 overflow-x-hidden pt-16 md:pt-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Service Categories</h1>
              <p className="text-slate-500">Manage categories. Services are added by providers.</p>
            </div>
          </div>
          <Button onClick={openAddCategory} className="bg-slate-900 hover:bg-slate-800" data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Category
          </Button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Services under each category are added by service providers, not by admin.
          </p>
        </div>

        {/* Active Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeCategories.map(cat => {
            const IconComp = availableIcons.find(i => i.name === cat.icon)?.component || Wrench;
            const storedColor = availableColors.find(c => c.name === cat.icon_color);
            const colorClass = storedColor 
              ? `${storedColor.bg} ${storedColor.text}` 
              : 'bg-slate-100 text-slate-600';
            const stats = categoryStats[cat.id] || { count: 0, lowestPrice: null };

            return (
              <div key={cat.id} className="bg-white rounded-2xl shadow-sm p-4" data-testid={`category-card-${cat.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${colorClass} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <IconComp className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900">{cat.name}</h3>
                    <p className="text-sm text-slate-500 truncate">{cat.description}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {stats.lowestPrice !== null ? (
                        <span className="text-xs text-amber-600 font-medium">Starting at ₱{stats.lowestPrice.toLocaleString()}</span>
                      ) : (
                        <span className="text-xs text-slate-400">No services yet</span>
                      )}
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                        {stats.count} provider services
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-stone-100">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditCategory(cat)} data-testid={`edit-cat-${cat.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteCategory(cat.id)} data-testid={`delete-cat-${cat.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Inactive Categories */}
        {inactiveCategories.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-slate-500 mb-4">Deactivated Categories</h2>
            <div className="space-y-2">
              {inactiveCategories.map(cat => (
                <div key={cat.id} className="bg-white/60 rounded-xl p-4 flex items-center justify-between opacity-60">
                  <div>
                    <p className="font-medium text-slate-700">{cat.name}</p>
                    <p className="text-xs text-slate-400">{cat.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleRestoreCategory(cat.id)} data-testid={`restore-cat-${cat.id}`}>
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" 
                      onClick={() => handlePermanentDeleteCategory(cat.id, cat.name)} 
                      data-testid={`permanent-delete-cat-${cat.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Category Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Name</Label>
              <Input value={catForm.name} onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))} className="h-12 mt-1.5" placeholder="e.g., Plumbing" data-testid="cat-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={catForm.description} onChange={e => setCatForm(p => ({ ...p, description: e.target.value }))} className="h-12 mt-1.5" placeholder="Brief description" data-testid="cat-desc-input" />
            </div>
            <div>
              <Label>Icon {usedIcons.length > 0 && <span className="text-xs text-slate-400 ml-1">(dimmed = already in use)</span>}</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {availableIcons.map(ic => {
                  const isUsed = usedIcons.includes(ic.name) && catForm.icon !== ic.name && editingCategory?.icon !== ic.name;
                  const selectedColor = availableColors.find(c => c.name === catForm.icon_color) || availableColors[0];
                  return (
                    <button key={ic.name} onClick={() => setCatForm(p => ({ ...p, icon: ic.name }))} className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-colors ${catForm.icon === ic.name ? `border-amber-500 ${selectedColor.bg}` : isUsed ? 'border-stone-100 bg-stone-50 opacity-40' : 'border-stone-200 hover:border-stone-300'}`} data-testid={`icon-${ic.name}`} title={ic.name}>
                      <ic.component className={`w-5 h-5 ${catForm.icon === ic.name ? selectedColor.text : ''}`} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Icon Color</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {availableColors.map(color => {
                  const SelectedIcon = availableIcons.find(i => i.name === catForm.icon)?.component || Wrench;
                  return (
                    <button 
                      key={color.name} 
                      onClick={() => setCatForm(p => ({ ...p, icon_color: color.name }))} 
                      className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-all ${color.bg} ${catForm.icon_color === color.name ? 'border-slate-900 ring-2 ring-slate-900/20 scale-110' : 'border-transparent hover:scale-105'}`} 
                      data-testid={`color-${color.name}`} 
                      title={color.label}
                    >
                      <SelectedIcon className={`w-5 h-5 ${color.text}`} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> Base prices are automatically calculated from the lowest service price listed by providers in this category.
              </p>
            </div>
            <Button onClick={handleSaveCategory} disabled={saving} className="w-full h-12 bg-slate-900 hover:bg-slate-800" data-testid="save-category-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : editingCategory ? 'Update Category' : 'Create Category'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
